import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  setColourShade,
  SIGNOL_COLOUR_NAMES,
} from "@signolteam/shared/colours";
import { AppUtils, useIsMobileWidth, useIsTabletWidth } from "@src/utils";

const Tabs = ({
  tabs,
  layout,
  gap = false,
  selected = 0,
  onChange = undefined,
  allowOverflow = false,
}) => {
  const isMobile = useIsMobileWidth();
  const isTablet = useIsTabletWidth();
  const [selectedTab, setSelectedTab] = useState(selected || 0);
  const currentColour = tabs[selectedTab]?.colour || "neutral";

  if (!tabs)
    return (
      <div className="text-center p-6">
        Error while rendering event details...
      </div>
    );

  return (
    <div
      id={"tab-section-wrapper"}
      className={`flex flex-col ${allowOverflow ? "" : "overflow-hidden"}`}
    >
      <div
        className={`px-2 md:px-4 flex overflow-y-auto border-b-8
            border-${setColourShade(currentColour, 500)}
            ${gap ? "gap-2" : ""}
            ${
              layout === "spread"
                ? "w-full"
                : layout === "right"
                  ? "justify-end"
                  : "justify-start"
            }`}
      >
        {tabs.map((tab, idx) => {
          const isSelectedTab = idx === selectedTab;
          const bgClass = setColourShade(tab.colour, isSelectedTab ? 500 : 200);
          return (
            <button
              type="button"
              key={`tab-button-${idx}`}
              className={`
                m-0 px-5 md:px-6 py-2 bg-${bgClass} rounded-t-[20px] text-black font-bold 
                text-ellipsis whitespace-nowrap outline-0 overflow-hidden
                ${isSelectedTab ? "" : "cursor-pointer"}
                ${isMobile ? "text-base" : isTablet ? "text-md" : "text-xl"}
                ${layout === "spread" ? "flex-1 text-center" : ""}
              `}
              onClick={() => {
                if (!isSelectedTab) {
                  if (typeof onChange === "function") onChange(idx);
                  setSelectedTab(idx);
                }
              }}
            >
              {tab.name}
            </button>
          );
        })}
      </div>
      {/* **Only render the selected tab's content** */}
      <div
        key={`tab-content-${selectedTab}`}
        id={`tab-content--`}
        className="bg-canvas grow"
      >
        {tabs[selectedTab]?.content}
      </div>
    </div>
  );
};

export const tabsControls = {
  tabs: {
    propType: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        colour: PropTypes.oneOf(SIGNOL_COLOUR_NAMES).isRequired,
        content: PropTypes.node.isRequired,
      }),
    ).isRequired,
    description: "Array of tab objects containing name, colour and content",
    controlType: "object",
    defaultValue: [
      {
        name: "Tab 1",
        colour: "blue",
        content: <div className="p-4">Content for Tab 1</div>,
      },
      {
        name: "Tab 2",
        colour: "green",
        content: <div className="p-4">Content for Tab 2</div>,
      },
    ],
  },
  layout: {
    propType: PropTypes.string,
    controlType: "select",
    options: ["left", "right", "spread"],
  },
  gap: {
    propType: PropTypes.bool,
    description: "Gap between the tabs",
    controlType: "boolean",
  },
  onChange: {
    propType: PropTypes.func,
    description: "Optional function triggered when changing tabs",
  },
};

Tabs.propTypes = AppUtils.getPropTypeFromControls(tabsControls);

export default Tabs;
